import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { includes, noop, find } from 'lodash';
import classnames from 'classnames';

import { unitGroupList } from 'config/units';
import Info from 'assets/images/icons/info.svg';
import { IconText } from 'components/core/IconText';
import { UnitList, BookableUnitDetails } from 'components/unit/UnitList';
import { SelectOptionsGroup } from 'components/core/SelectOptionsGroup';

import './filterableUnitList.scss';
import { Text } from '../../core/Typography/Typography';

export function FilterableUnitList({
	className,
	facility,
	phone,
	units,
	unitSizeFilters,
	unitDetailsComponent = BookableUnitDetails,
	unitDetailsComponentProps = noop,
}) {
	const [activeUnitSizeFilters, setActiveUnitSizeFilters] = useState(
		unitSizeFilters || [],
	);
	const [showUnitSizeFilterAlert, setShowUnitSizeFilterAlert] = useState(
		!!(unitSizeFilters && unitSizeFilters.length),
	);

	const handleUnitFilter = (selectedUnitSizes) => {
		setActiveUnitSizeFilters(selectedUnitSizes);
		setShowUnitSizeFilterAlert(false);
	};

	const unitGroups = useMemo(
		() =>
			unitGroupList.map((group) => ({
				...group,
				disabled: !find(units, (unit) => unit.sizeBucket === group.value),
			})),
		[units],
	);

	const filteredUnits = useMemo(
		() =>
			activeUnitSizeFilters.length === 0
				? units
				: units.filter((unit) =>
						includes(activeUnitSizeFilters, unit.sizeBucket),
				  ),
		[activeUnitSizeFilters, units],
	);

	return (
		<div className={classnames(className, 'filterable-unit-list')}>
			{showUnitSizeFilterAlert && (
				<IconText
					className="unit-size-filtered-msg"
					type="info"
					src={Info}
				>
					We've filtered units based on your previously selected size
					({activeUnitSizeFilters.join(', ')}). Use the filters below
					to alter your size selection.
				</IconText>
			)}
			<SelectOptionsGroup
				id="unit-filter"
				options={unitGroups}
				onChange={handleUnitFilter}
				selected={activeUnitSizeFilters}
				name="unit-filter"
				groupType="multiple"
			/>

			{facility?.showFeeWarning && !!facility?.fees?.totalFees?.value && (
				<div className="fee-disclaimer-text">
					<Text weight="standard" color="white">
						Select a unit to see full pricing with additional fees
						displayed.
					</Text>
				</div>
			)}

			<UnitList
				units={filteredUnits}
				facility={facility}
				phone={phone}
				unitDetailsComponent={unitDetailsComponent}
				unitDetailsComponentProps={unitDetailsComponentProps}
			/>
		</div>
	);
}

FilterableUnitList.propTypes = {
	facility: PropTypes.object.isRequired,
	phone: PropTypes.object,
	/** The component to render the unit with */
	unitDetailsComponent: PropTypes.func,
	unitDetailsComponentProps: PropTypes.func,
	className: PropTypes.string,
	units: PropTypes.arrayOf(PropTypes.object).isRequired,
	unitSizeFilters: PropTypes.arrayOf(PropTypes.string),
};

export default FilterableUnitList;
